import React from 'react';

import { Path } from '~/paths';
import { ChapterWrapper } from '~/ChapterWrapper';
import Footer from '~/Footer';
import { ELink } from '~/ExternalLink';


export const HypothesisTestingMeta = {
    title: "Testy hypotéz",
    shortTitle: "Testy hypotéz",
    path: Path.hypothesis_testing,
    element: (sectionNumber:string) => <HypothesisTesting sectionNumber={sectionNumber}/>,
    sectionNumber: "",
}

const TOCSpec = [
    "hypt-terminology",
    "hypt-principle",
    "hypt-use",
    "hypt-1",
    "hypt-alt",
    "hypt-eval",
    "hypt-errors",
    "hypt-pvalue",
];


export function HypothesisTesting({sectionNumber}: {sectionNumber: string}) {

    return (
        <ChapterWrapper sectionNumber={sectionNumber} title={HypothesisTestingMeta.title} TOCSpec={TOCSpec}>

            <p><i>Stručná verze (prozatím)</i></p>

            Viz přednášky (poznámky, videa), a nebo např.

            &nbsp;<ELink href={"https://www.youtube.com/watch?v=8JIe_cz6qGA"}>youtube</ELink>,

            nebo

            &nbsp;<ELink href={"https://is.muni.cz/do/rect/el/estud/prif/ps13/prav_stat/web_2/pages/05-test-statist-hypotez.html"}>kurz MUNI</ELink>

            &nbsp;(matematičtější), nebo

            &nbsp;<ELink href={"https://www.stat.berkeley.edu/~stark/SticiGui/Text/testing.htm"}>Stark</ELink>.

            <h2 id="hypt-terminology">Terminologie</h2>
            <i>(stejná jako u intervalových odhadů)</i>
            <table className='table-interval-estimation simple-table'>
                <tbody>
                    <tr>
                        <td>{"$\\alpha$"}</td>
                        <td><i>hladina významnosti</i></td>
                        <td>typicky {"$\\alpha = 0.05 \\ (5\\%)$"}, nebo {"$\\alpha = 0.01 \\ (1\\%)$"}</td>
                    </tr>
                    <tr>
                        <td>{"$1 - \\alpha$"}</td>
                        <td><i>spolehlivost</i></td>
                        <td>typicky {"$1 - \\alpha = 0.95 \\ (95\\%)$"}, nebo {"$1 - \\alpha = 0.99 \\ (99\\%)$"}</td>
                    </tr>
                </tbody>
            </table>

            <h2 id="hypt-principle">Princip</h2>
            <p>
            Měříme nějakou veličinu, u které např. tušíme, že má normální rozdělení, jen nevíme jaká je střední hondota a rozptyl.
            Dejme tomu, že máme představu, že střední hodnota je {"$\\mu = 10$"}.
            </p>
            <p>
            Provedeme {"$n$"} měření, a vyjde nám průměr {"$\\bar{x}$"} a rozptyl {"$s^2$"}. Pokud je rozptyl relativně malý,
            a průměr {"$\\bar{x}$"} je daleko od {"$10$"} (např. vyšlo {"$\\bar{x} = 3$"}), tak výsledek je vzhledem k
            předpokladu velmi nepravděpodobný. Neřekneme si, že máme zrovna smůlu/štěstí, ale výsledek interpretujeme jako
            indikaci, že náš předpoklad {"$\\mu = 10$"} je pravděpodobně chybný, a ve skutečnosti je průměr jiný, menší.
            </p>
            <p>
            Zbytek je o kvantifikaci - jak tohle udělat korektně
            (nepravděpodobný výsledek = výsledek s pravděpodobností menší než např. {"$\\alpha = 0.05$"}).
            </p>
            <p>Původní představě se říká <i>nulová hypotéza</i> {"$H_0: \\mu = 10$"}, pokud ji zamítneme, tak ve
            prospěch <i>alternativní hypotézy</i>, zde {"$H_1: < 10$"}.
            </p>

            <h2 id="hypt-use">Použití</h2>
            <p>
            Metodiku používáme tak, že {"$H_0$"} je hypotéza, kterou chceme <strong>vyvrátit</strong>, a {"$H_1$"} je hypotéza, kterou chceme potvrdit (ne naopak).
            </p>

            <h2 id="hypt-1">Metodika</h2>
            <p>Mějme výběr z normálního rozdělení s neznámým průměrem {"$\\mu$"} a rozptylem {"$\\sigma^2$"}.
            Statistika {"$T$"} má Studentovo rozdělení s {"$n-1$"} stupni volnosti (viz kapitiola Intervalové odhady nebo Studentovo rozdělení):
            {`$$
                T = \\frac{\\bar{X} - \\mu}{\\frac{S}{\\sqrt{n}}} \\sim t(n-1),
            $$`}
            platí tedy
            {`\\begin{align*}
                P\\left(T > t_{1-\\alpha}(n-1)\\right) & = \\alpha, \\ \\  \\textrm{(pravý chvost)} \\\\
                P\\left(T < t_{\\alpha}(n-1)\\right)   & = \\alpha, \\ \\  \\textrm{(levý chvost)} \\\\
                P\\left( T < t_{\\alpha/2}(n-1) \\lor T > t_{1-\\alpha/2}(n-1)\\right) & = \\alpha, \\ \\ \\textrm{(levý a pravý chvostík)} \\\\
            \\end{align*}`}
            [obrázek] <br/>
            Pokud určíme hodnotu {"$T$"} z naměřených dat, a zjistíme, že patří do daných oblastí (s malou pravděpodobností {"$\\alpha$"}),
            tak zamítneme nulovou hypotézu {"$H_0$"} a přijmeme alternativní hypotézu {"$H_1$"}.
            </p>
            <div className="example" style={{backgroundColor: "#ffffef"}}>
                <p>
                Příklad: Dětská výživa by měla obsahovat méně než {"$10$"} mikrogramů dané škodlivé látky na kilogram.
                Ve vzorku 6 balení byly změřeny tyto hodnoty (v mikrogramech na kilogram): <br/>
                {`$$
                    8.1, 8.7, 9.5, 11.3, 7.5, 6.0
                $$`}
                Hypotézu {"$H_0: \\mu = 10$"} (průměrná hodnota je {"$10$"} mikrogramů) chceme zamítnout ve prospěch
                alternativní hypotézy {"$H_1: \\mu < 10$"} (průměrná hodnota je menší než {"$10$"} mikrogramů).
                Pokud {"$H_0$"} nezamítneme, stahujeme výživu z trhu.
                </p>
                <p>
                Určíme:
                {`$$
                \\bar{x} = 8.51\\bar{6}, \\quad s = 1.805, \\quad n = 6, \\quad n - 1 = 5, \\quad T = \\frac{8.51\\bar{6} - 10}{\\frac{1.805}{\\sqrt{6}}} = -2.013
                $$`}
                Dejme tomu, že zvolíme hladinu významnosti {"$\\alpha = 0.05$"}.
                Potom kvantil
                {`$$
                    t_{\\alpha}(n-1) = - t_{1-\\alpha}(n-1) = - t_{0.95}(5) = -2.015
                $$`}
                Nenastalo, že by {"$T = -2.013$"} bylo menší než {"$-2.015$"}, takže nenastal jev s pravděpodobností menší než {"$\\alpha = 0.05$"}. Výsledek
                tedy na úrovni {"$\\alpha = 0.05$"} neodporuje předpokladu, že by průměrná hodnota mohla být byla {"$10$"} mikrogramů,
                a nulovou hypotézu {"$H_0: \\mu = 10$"} tedy nezamítáme.
                </p>
            </div>

            <h2 id="hypt-alt">Výběr alternativní hypotézy</h2>

            <p>
            Alternativní hypotézu vybíráme buď jednostrannou (do leva nebo do prava), nebo oboustrannou, podle logiky dané situace.
            Např. v příkladu výše jsme intuitivně zvolili jednostrannou alternativní hypotézu {"$H_1: \\mu < 10$"},
            protože na ni ukazovala data, a zároveň to je hypotéza, kterou bychom chtěli potvrdit.
            </p>

            <h2 id="hypt-eval">Vyhodnocení</h2>
            <p>
            Nulová hypotéza: {"$H_0: \\mu = \\mu_0$"}; ({"$\\ \\mu_0$"} je nějaká konstanta, např. {"$10$"} v příkladu výše).
            </p>
            <table className='table-interval-estimation simple-table'>
                <thead>
                    <tr>
                        <td>{"$H_1$"}</td>
                        <td style={{textAlign: "center"}}>kritický obor {"$W$"}</td>
                        <td>pozn.</td>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>{"$H_1: \\mu < \\mu_0$"}</td>
                        <td style={{textAlign: "left"}}>{"$(\\infty, -t_{\\alpha}(n-1) \\rangle$"}</td>
                        <td>(levý chvost)</td>
                    </tr>
                    <tr>
                        <td>{"$H_1: \\mu > \\mu_0$"}</td>
                        <td style={{textAlign: "right"}}>{"$\\langle t_{1-\\alpha}(n-1), \\infty)$"}</td>
                        <td>(pravý chvost)</td>
                    </tr>
                    <tr>
                        <td>{"$H_1: \\mu \\neq \\mu_0$"}</td>
                        <td style={{textAlign: "center"}}>{"$(\\infty, -t_{\\alpha/2}(n-1) \\rangle \\ \\ \\cup \\ \\ \\langle t_{1-\\alpha/2}(n-1), \\infty)$"}</td>
                        <td>(levý a pravý chvostík)</td>
                    </tr>
                </tbody>
            </table>
            <p>
            Ve všech případech platí, že {"$P(T \\in W) = \\alpha$"}, a pokud {"$T \\in W$"}, tak zamítáme
            nulovou hypotézu {"$H_0$"} ve prospěch alternativní hypotézy {"$H_1$"}.
            </p>

            <h2 id="hypt-errors">Chyba I. a II. druhu</h2>
            <p>Prakticky mohou nastat následující čtyři scénáře:</p>
            <table className='simple-table'>
                <thead>
                    <tr>
                        <td></td>
                        <td>{"$H_0$"} platí</td>
                        <td>{"$H_0$"} neplatí</td>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>{"$H_0$"} zamítáme</td>
                        <td>
                            Chyba I. druhu ({"$P = \\alpha$"})
                        </td>
                        <td>
                            OK
                        </td>
                    </tr>
                    <tr>
                        <td>{"$H_0$"} nezamítáme</td>
                        <td>OK</td>
                        <td>
                            Chyba II. druhu
                        </td>
                    </tr>
                </tbody>
            </table>
            <p>
            Chyba I. druhu je chyba, kdy zamítneme nulovou hypotézu, i když ve skutečnosti platí. V příkladu
            výše by to znamenalo, že bychom dětskou výživu považovali za bezpečnou, i když ve skutečnosti není.
            To je velké riziko, a proto chceme mít tuto chybu pod kontrolou - víme, s jakou pravděpodobností {"$\\alpha$"} může nastat.
            Jedná-li se o citlivou záležitost (jako je zde), pak klidně volíme malé {"$\\alpha$"}, např. {"$\\alpha = 0.01$"} nebo {"$\\alpha = 0.001$"}.
            </p>
            <p>
            Chyba II. druhu je chyba, kdy nezamítneme nulovou hypotézu, i když ve skutečnosti neplatí. V příkladu výše by to znamenalo,
            že bychom dětskou výživu považovali za nebezpečnou stáhli z trhu, i když ve skutečnosti nebezpečná není.
            To nepředstavuje vyloženě riziko, ale určitou ekonomickou ztrátu / neefiktivnost.
            Chyba II. druhu má nějakou pravděpodobnost {"$\\beta$"}, ale ta závisí na skutečné hodnotě průměru {"$\\mu$"},
            může tedy být velká nebo malá, ale my ji v principu neovlivníme a neznáme. Oproti tomu chybu I. druhu {"$\\alpha$"}
            máme apriori pod kontrolou.
            </p>
            <p>Pokud volíme hodně malé {"$\\alpha$"}, např. {"$\\alpha = 0.001$"}, pak je vhodné provést co největší výběr
            (mít co největší {"$n$"}) - tím se sníží výběrový rozptyl, a sníží se pravděpodobnost chyby II. druhu.
            Při malém výběru bychom téměř nic nezamítli.
            </p>

            <h2 id="hypt-pvalue">p-hodnota</h2>
            <p>
            Při vyhodnocování, zda statistika padla do kritického oboru, je možné porovnávat buď kvantily, nebo pravděpodobnosti.
            </p>
            <p>Dejme tomu, že máme jednostrannou hypotézu {"$H_1: \\mu < \\mu_0$"},
            a vyjde nám hodnota {"$T = t_p$"}, která je p-kvantilem Studentova rozdělení s {"$n-1$"} stupni volnosti.
            Následující porovnání jsou ekvivalentní:
            {`$$
                t_p < t_{\\alpha}(n-1) \\quad \\iff \\quad p < \\alpha
            $$`}
            Výše jsme použili porovnání kvantilů. Hondotu {"$p$"} bychom pro dané {"$T$"} určili z tabulky kvantilů Studentova rozdělení.
            </p>

            <p>
            Máme předepsané {"$\\alpha$"} a spočítáme {"$T = t_p$"}; abychom provedli test, musíme provést porovnání
            aspoň jedním z těchto způsobů - v levém způsobu porovnáváme {"$t_p$"} s kvantilem {"$t_{\\alpha}(n-1)$"} (který odečteme z tabulky),
            v pravém způsobu porovnáváme {"$\\alpha$"} s pravděpodobností {"$p$"} (kterou odečteme z tabulky).
            </p>


            <Footer/>
        </ChapterWrapper>
    );
}

